import "./styles/main.scss";
import {
  filterActivities,
  groupActivitiesByDay,
  toRenderableActivity,
} from "./activities.js";
import {
  escapeHtml,
  formatAbsoluteDate,
  formatActionWithStatus,
  formatMethod,
  formatStatusFull,
  getTagValues,
  isCancelled,
} from "./formatting.js";
import {
  getCurrentDictionary,
  applyLanguage as i18nApplyLanguage,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
  type Translations,
  t,
  translate,
} from "./i18n.js";
import { initLoaderRestoreReset, showButtonLoader } from "./page.js";
import {
  handleUnauthorized,
  startActivityTracking,
  startSessionKeepalive,
  stayActiveAfterHeartbeat,
} from "./session-keepalive.js";
import { safeSetItem } from "./storage.js";
import {
  applyThemeMode,
  getCurrentTheme,
  getStoredTheme,
  THEME_CYCLE,
  THEME_STORAGE_KEY,
} from "./theme.js";
import en from "./translations/activity.en.json";
import nb from "./translations/activity.nb.json";
import nn from "./translations/activity.nn.json";
import type { Activity, PaginatedActivityResponse } from "./types.js";

export { LANG_STORAGE_KEY, SUPPORTED_LANGUAGES } from "./i18n.js";
export { getCurrentTheme, THEME_STORAGE_KEY };

const dictionaries: Record<SupportedLanguage, Translations> = { en, nb, nn };

// ===========================================
// Configuration
// ===========================================

const API_BASE = "/api";
const SEARCH_DEBOUNCE_MS = 200;
const LOGOS_IMAGE_PATH = /^\/logos\/v1\/[A-Za-z0-9_-]+\/logo\/medium$/;

// ===========================================
// Alert Banner SVG Icons
// ===========================================

const ALERT_ICON_INFO = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M12 2.25A9.75 9.75 0 1 0 21.75 12 9.76 9.76 0 0 0 12 2.25Zm.75 14.25a.75.75 0 0 1-1.5 0v-4.5a.75.75 0 0 1 1.5 0v4.5Zm-.75-7.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z" fill="currentColor"/>
</svg>`;

const ALERT_ICON_SUCCESS = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <circle cx="12" cy="12" r="10" fill="#39134c"/>
  <path d="M12 2.25A9.75 9.75 0 1 0 21.75 12 9.76 9.76 0 0 0 12 2.25Zm4.28 7.28-5.25 5.25a.75.75 0 0 1-1.06 0l-2.25-2.25a.75.75 0 1 1 1.06-1.06L10.5 13.19l4.72-4.72a.75.75 0 1 1 1.06 1.06Z" fill="#67e702"/>
</svg>`;

// ===========================================
// Theme Toggle
// ===========================================

type ThemeMode = "system" | "light" | "dark";

export function updateThemeButtonLabel(mode: ThemeMode): void {
  const btn = document.getElementById("theme-toggle");
  if (btn) {
    btn.textContent = t(`footer_theme_${mode}`);
  }
}

export function applyTheme(mode: ThemeMode): void {
  applyThemeMode(mode);
  updateThemeButtonLabel(mode);
}

export function initThemeToggle(): void {
  applyTheme(getStoredTheme());

  const btn = document.getElementById("theme-toggle");
  if (btn) {
    btn.addEventListener("click", () => {
      const current = getCurrentTheme();
      const idx = THEME_CYCLE.indexOf(current);
      const next = THEME_CYCLE[(idx + 1) % THEME_CYCLE.length];
      applyTheme(next);
      safeSetItem(THEME_STORAGE_KEY, next);
    });
  }
}

// ===========================================
// Language Selector
// ===========================================

// Tracks whether Space keydown on the toggle was handled, so the
// subsequent native click (fired on keyup for buttons) can be suppressed
// to prevent open→close flicker.
let langToggleSpaceHandled = false;

export function applyLanguage(lang: SupportedLanguage): void {
  if (!(SUPPORTED_LANGUAGES as readonly string[]).includes(lang)) {
    return;
  }
  i18nApplyLanguage(lang, dictionaries);
  updateLangButtonLabel();
  updateLangListSelection();
  updateThemeButtonLabel(getCurrentTheme());
  rerenderLocalizedViews();
}

function rerenderLocalizedViews(): void {
  if (allActivities.length > 0) {
    renderGroupedActivities(allActivities);
  }
  const state = history.state as HistoryState | null;
  if (state?.view === "detail") {
    const event = allActivities[state.index];
    if (event) {
      renderDetail(event);
    }
  }
}

export function openLangSelector(): void {
  const btn = document.getElementById("lang-toggle");
  const list = document.getElementById("lang-list");
  if (btn) btn.setAttribute("aria-expanded", "true");
  if (list) list.classList.add("lang-selector__list--open");
}

export function closeLangSelector(): void {
  const btn = document.getElementById("lang-toggle");
  const list = document.getElementById("lang-list");
  if (btn) btn.setAttribute("aria-expanded", "false");
  if (list) list.classList.remove("lang-selector__list--open");
  langToggleSpaceHandled = false;
}

function getLangOptions(): HTMLElement[] {
  const list = document.getElementById("lang-list");
  if (!list) return [];
  return Array.from(list.querySelectorAll<HTMLElement>('[role="option"]'));
}

function focusSelectedOrFirstOption(): void {
  const opts = getLangOptions();
  if (opts.length === 0) return;
  const selected = opts.find((o) => o.getAttribute("aria-selected") === "true");
  const target = selected ?? opts[0];
  setLangOptionFocus(target);
}

function setLangOptionFocus(option: HTMLElement): void {
  const opts = getLangOptions();
  for (const o of opts) {
    o.setAttribute("tabindex", o === option ? "0" : "-1");
  }
  option.focus();
}

function handleLangListKeydown(e: KeyboardEvent): void {
  const opts = getLangOptions();
  if (opts.length === 0) {
    if (e.key === "Escape") {
      closeLangSelector();
      document.getElementById("lang-toggle")?.focus();
      return;
    }
    if (e.key === "Tab") {
      closeLangSelector();
      return;
    }
    return;
  }

  const focused = document.activeElement as HTMLElement | null;
  const idx = focused ? opts.indexOf(focused) : -1;

  switch (e.key) {
    case "ArrowDown": {
      e.preventDefault();
      if (idx < 0) {
        setLangOptionFocus(opts[0]);
      } else if (idx < opts.length - 1) {
        setLangOptionFocus(opts[idx + 1]);
      }
      break;
    }
    case "ArrowUp": {
      e.preventDefault();
      if (idx < 0) {
        setLangOptionFocus(opts[0]);
      } else if (idx > 0) {
        setLangOptionFocus(opts[idx - 1]);
      }
      break;
    }
    case "Home": {
      e.preventDefault();
      setLangOptionFocus(opts[0]);
      break;
    }
    case "End": {
      e.preventDefault();
      setLangOptionFocus(opts[opts.length - 1]);
      break;
    }
    case "Enter":
    case " ": {
      e.preventDefault();
      const option =
        idx >= 0
          ? opts[idx]
          : opts.find((o) => o.getAttribute("aria-selected") === "true");
      if (option) {
        const lang = option.getAttribute("data-lang");
        if (lang && (SUPPORTED_LANGUAGES as readonly string[]).includes(lang)) {
          applyLanguage(lang as SupportedLanguage);
        }
      }
      closeLangSelector();
      document.getElementById("lang-toggle")?.focus();
      break;
    }
    case "Escape": {
      closeLangSelector();
      document.getElementById("lang-toggle")?.focus();
      break;
    }
    case "Tab": {
      closeLangSelector();
      break;
    }
    default:
      break;
  }
}

function updateLangButtonLabel(): void {
  const btn = document.getElementById("lang-toggle");
  if (btn) {
    const dict = getCurrentDictionary();
    const lang = dict?.language ?? "en";
    btn.textContent = t(`lang_${lang}`);
  }
}

function updateLangListSelection(): void {
  const list = document.getElementById("lang-list");
  if (!list) return;
  const dict = getCurrentDictionary();
  const currentLang = dict?.language ?? "en";
  for (const item of list.querySelectorAll<HTMLElement>("[data-lang]")) {
    const isSelected = item.getAttribute("data-lang") === currentLang;
    item.setAttribute("aria-selected", String(isSelected));
  }
  updateLangRovingTabindex();
}

function updateLangRovingTabindex(): void {
  const opts = getLangOptions();
  if (opts.length === 0) return;
  const selected = opts.find((o) => o.getAttribute("aria-selected") === "true");
  const active = selected ?? opts[0];
  for (const o of opts) {
    o.setAttribute("tabindex", o === active ? "0" : "-1");
  }
}

export function initLanguageToggle(): void {
  updateLangButtonLabel();
  updateLangListSelection();
  updateLangRovingTabindex();

  const btn = document.getElementById("lang-toggle");
  const list = document.getElementById("lang-list");
  if (!btn || !list) return;

  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    if (langToggleSpaceHandled && e.detail === 0) {
      langToggleSpaceHandled = false;
      return;
    }
    langToggleSpaceHandled = false;
    const open = list.classList.contains("lang-selector__list--open");
    if (open) {
      closeLangSelector();
    } else {
      openLangSelector();
    }
  });

  btn.addEventListener("keydown", (e) => {
    if (e.key === " ") {
      e.preventDefault();
      langToggleSpaceHandled = true;
      setTimeout(() => {
        langToggleSpaceHandled = false;
      }, 300);
      if (!list.classList.contains("lang-selector__list--open")) {
        openLangSelector();
      }
      focusSelectedOrFirstOption();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!list.classList.contains("lang-selector__list--open")) {
        openLangSelector();
      }
      focusSelectedOrFirstOption();
    }
  });

  list.addEventListener("click", (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>(
      "[data-lang]",
    );
    if (!target) return;
    const lang = target.getAttribute("data-lang");
    if (lang && (SUPPORTED_LANGUAGES as readonly string[]).includes(lang)) {
      applyLanguage(lang as SupportedLanguage);
      closeLangSelector();
    }
  });

  list.addEventListener("keydown", (e) => {
    handleLangListKeydown(e);
  });

  document.addEventListener("click", (e) => {
    const selector = document.querySelector(".lang-selector");
    if (selector && !selector.contains(e.target as Node)) {
      closeLangSelector();
    }
  });
}

// ===========================================
// Auth Helpers
// ===========================================

class AuthRequiredError extends Error {
  constructor() {
    super("Authentication required");
    this.name = "AuthRequiredError";
  }
}

async function checkSession(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/session/heartbeat`, {
      credentials: "include",
    });
    if (response.status === 401) return false;
    return true;
  } catch {
    return true;
  }
}

// ===========================================
// DOM Helpers
// ===========================================

function getRequiredElement<T extends HTMLElement = HTMLElement>(
  id: string,
): T {
  const el = document.getElementById(id);
  if (!el) {
    throw new Error(`Required element #${id} not found in DOM`);
  }
  return el as T;
}

// ===========================================
// DOM Elements
// ===========================================

// Views
const listViewEl = getRequiredElement("list-view");
const detailViewEl = getRequiredElement("detail-view");

// List view elements
const loadingEl = getRequiredElement("loading");
const errorEl = getRequiredElement("error");
const contentEl = getRequiredElement("content");
const activityListEl = getRequiredElement("activity-list");
const emptyStateEl = getRequiredElement("empty-state");
const searchEmptyEl = getRequiredElement("search-empty");
const scrollSentinelEl = getRequiredElement("scroll-sentinel");
const retryBtn = getRequiredElement("retry");
const demoBannerEl = getRequiredElement("demo-banner");
const searchContainerEl = getRequiredElement("search-container");
const searchInputEl = getRequiredElement<HTMLInputElement>("search-input");
const searchClearEl = getRequiredElement<HTMLButtonElement>("search-clear");
const searchDisclaimerEl = getRequiredElement("search-disclaimer");

// Detail view elements
const detailBackBtn = getRequiredElement("detail-back");
const detailContentEl = getRequiredElement("detail-content");

// Landing view elements
const landingViewEl = getRequiredElement("landing-view");
const pageContentEl = getRequiredElement("page-content");
const logoutFormEl = document.querySelector(".logout-form") as HTMLElement;
const initLoadingEl = getRequiredElement("init-loading");

// Alert banner elements
const alertBannerEl = getRequiredElement("alert-banner");
const alertIconEl = getRequiredElement("alert-icon");
const alertTextEl = getRequiredElement("alert-text");

// ===========================================
// State
// ===========================================

let allActivities: Activity[] = [];
let archiveLoaded = false;
let archiveCursor: string | null = null;
let searchQuery = "";
let listScrollPosition = 0;
let isLoadingMore = false;
let scrollObserver: IntersectionObserver | null = null;
const failedLogoPaths = new Set<string>();

// ===========================================
// View Switching
// ===========================================

type View = "list" | "detail" | "landing";

type HistoryState = { view: "list" } | { view: "detail"; index: number };

function showView(view: View): void {
  initLoadingEl.hidden = true;
  landingViewEl.hidden = view !== "landing";
  listViewEl.hidden = view !== "list";
  detailViewEl.hidden = view !== "detail";

  pageContentEl.classList.toggle("page__content--landing", view === "landing");

  // Hide logout form on landing view (user is not logged in)
  if (logoutFormEl) {
    logoutFormEl.hidden = view === "landing";
  }

  if (view === "list") {
    window.scrollTo(0, listScrollPosition);
  }
}

// ===========================================
// UI State Management
// ===========================================

type UIState = "loading" | "error" | "loaded" | "empty";

function setUIState(state: UIState): void {
  loadingEl.hidden = state !== "loading";
  errorEl.hidden = state !== "error";
  contentEl.hidden = state === "loading" || state === "error";
  emptyStateEl.hidden = state !== "empty";
  searchContainerEl.hidden = state !== "loaded" && state !== "empty";
  searchDisclaimerEl.hidden = state !== "loaded" && state !== "empty";
  contentEl.setAttribute("aria-busy", state === "loading" ? "true" : "false");
}

// ===========================================
// Alert Banner
// ===========================================

function updateAlertBanner(): void {
  const params = new URLSearchParams(window.location.search);
  const loggedOut = params.get("logged_out") === "1";
  const sessionExpired = params.get("session_expired") === "1";

  // Nothing to show — leave the banner (and its classes) untouched so we never
  // strip styling off an already-visible banner or reveal an unstyled one.
  if (!loggedOut && !sessionExpired) {
    return;
  }

  // Reset variant classes before applying one so a message is never rendered
  // with both alert--success and alert--info (conflicting styles).
  alertBannerEl.classList.remove("alert--success", "alert--info");

  if (loggedOut) {
    alertTextEl.setAttribute("data-i18n", "logout_confirmation");
    alertTextEl.textContent = t("logout_confirmation");
    alertIconEl.innerHTML = ALERT_ICON_SUCCESS;
    alertBannerEl.classList.add("alert--success");
  } else {
    alertTextEl.setAttribute("data-i18n", "session_expired");
    alertTextEl.textContent = t("session_expired");
    alertIconEl.innerHTML = ALERT_ICON_INFO;
    alertBannerEl.classList.add("alert--info");
  }
  alertBannerEl.hidden = false;

  history.replaceState(history.state, "", window.location.pathname);
}

// ===========================================
// Rendering - List View
// ===========================================

/**
 * Markup for the logo the server published for this activity, to sit in the
 * icon/avatar slot. The image is purely presentational; when there is none the
 * slot stays empty and its CSS storefront fallback shows instead.
 */
function renderLogo(event: Activity): string {
  const url = renderableImageUrl(event.imageUrl);
  return url && !failedLogoPaths.has(url)
    ? `<img class="activity-logo" src="${escapeHtml(url)}" alt="" />`
    : "";
}

/**
 * The exact root-relative same-origin Logos namespace with the fixed `medium`
 * variant, or null when the published path does not match that contract.
 */
function renderableImageUrl(value: unknown): string | null {
  return typeof value === "string" && LOGOS_IMAGE_PATH.test(value)
    ? value
    : null;
}

/**
 * Drop a logo the browser could not load, which leaves the slot empty and
 * restores the same storefront fallback an activity without a logo gets.
 */
function dropFailedLogos(root: HTMLElement): void {
  for (const img of root.querySelectorAll<HTMLImageElement>(
    "img.activity-logo",
  )) {
    img.addEventListener(
      "error",
      () => {
        const path = renderableImageUrl(img.getAttribute("src"));
        if (path) failedLogoPaths.add(path);
        img.remove();
      },
      { once: true },
    );
  }
}

function renderActivityRow(event: Activity, index: number): string {
  const merchant = escapeHtml(event.actor?.name ?? t("unknown_merchant"));
  const meta = escapeHtml(formatActionWithStatus(event.action, event.status));
  const metaClass = isCancelled(event.status)
    ? "event-row__meta event-row__meta--cancelled"
    : "event-row__meta";

  return `
    <div class="event-row" role="listitem" tabindex="0" data-event-index="${index}">
      <div class="event-row__icon" aria-hidden="true">${renderLogo(event)}</div>
      <div class="event-row__content">
        <div class="event-row__merchant">${merchant}</div>
        <div class="${metaClass}">${meta}</div>
      </div>
      <div class="event-row__chevron" aria-hidden="true">&#8250;</div>
    </div>
  `;
}

function renderGroupedActivities(activities: Activity[]): void {
  const filtered = filterActivities(activities, searchQuery);

  if (filtered.length === 0 && searchQuery) {
    activityListEl.innerHTML = "";
    searchEmptyEl.hidden = false;
    scrollSentinelEl.hidden = true;
    disconnectObserver();
    return;
  }

  searchEmptyEl.hidden = true;
  const indexByRef = new Map<Activity, number>();
  for (let i = 0; i < activities.length; i++) {
    indexByRef.set(activities[i], i);
  }
  const groups = groupActivitiesByDay(filtered);
  const html = groups
    .map((group) => {
      const rows = group.activities
        .map((activity) => {
          const globalIndex = indexByRef.get(activity);
          if (globalIndex === undefined) return "";
          return renderActivityRow(activity, globalIndex);
        })
        .join("");

      return `
        <section class="month-group">
          <h2 class="month-group__header">${escapeHtml(group.label)}</h2>
          <div class="grouped-card" role="list">${rows}</div>
        </section>
      `;
    })
    .join("");

  activityListEl.innerHTML = html;
  dropFailedLogos(activityListEl);
  updateSentinelVisibility();
}

// ===========================================
// Rendering - Detail View
// ===========================================

function renderDetail(event: Activity): void {
  const merchant = escapeHtml(event.actor?.name ?? t("unknown_merchant"));
  const action = escapeHtml(formatActionWithStatus(event.action, event.status));
  const tagClass = isCancelled(event.status)
    ? "detail-header__tag detail-header__tag--neutral"
    : "detail-header__tag";
  const absoluteDate = escapeHtml(formatAbsoluteDate(event.time));

  const methodLabel = formatMethod(getTagValues(event.tags, "amr"));
  const method = methodLabel ? escapeHtml(methodLabel) : "";

  const rows: string[] = [];

  rows.push(`
    <div class="detail-row">
      <span class="detail-row__label">${escapeHtml(t("detail_timestamp"))}</span>
      <span class="detail-row__value">${absoluteDate}</span>
    </div>
  `);

  // Only activities that carry a status get a Status row: a blank one would
  // either read as a missing value or, worse, imply an outcome we were not told.
  if (event.status) {
    rows.push(`
      <div class="detail-row">
        <span class="detail-row__label">${escapeHtml(t("detail_status"))}</span>
        <span class="detail-row__value">${escapeHtml(formatStatusFull(event.status))}</span>
      </div>
    `);
  }

  if (method) {
    rows.push(`
      <div class="detail-row">
        <span class="detail-row__label">${escapeHtml(t("detail_method"))}</span>
        <span class="detail-row__value">${method}</span>
      </div>
    `);
  }

  const transactionId = escapeHtml(event.id);

  detailContentEl.innerHTML = `
    <div class="detail-header">
      <div class="detail-header__avatar" aria-hidden="true">${renderLogo(event)}</div>
      <h2 class="detail-header__title">${merchant}</h2>
      <span class="${tagClass}">${action}</span>
    </div>

    <div class="detail-card">
      ${rows.join("")}
    </div>

    <div class="detail-card detail-card--single">
      <div class="detail-row detail-row--stacked">
        <span class="detail-row__label">${escapeHtml(t("detail_transaction_id"))}</span>
        <span class="detail-row__sub">${transactionId}</span>
      </div>
    </div>

    <p class="detail-footer-note">${escapeHtml(t("detail_footer_note"))}</p>
  `;
  dropFailedLogos(detailContentEl);
}

// ===========================================
// API Calls
// ===========================================

// Activity display text is server-authored, so activity requests carry the
// language the page is already showing rather than the raw browser preference.
function activityRequestInit(): RequestInit {
  return {
    credentials: "include",
    headers: {
      "Accept-Language": getCurrentDictionary()?.language ?? "en",
    },
  };
}

async function fetchActivities(): Promise<PaginatedActivityResponse> {
  const response = await fetch(
    `${API_BASE}/me/activities?limit=200`,
    activityRequestInit(),
  );
  if (response.status === 401) {
    handleUnauthorized();
    throw new AuthRequiredError();
  }
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
}

async function fetchMoreActivities(): Promise<PaginatedActivityResponse> {
  const params = new URLSearchParams();
  params.set("limit", "10");
  if (archiveCursor) {
    params.set("before", archiveCursor);
  }
  const url = `${API_BASE}/me/activities?${params}`;
  const response = await fetch(url, activityRequestInit());
  if (response.status === 401) {
    handleUnauthorized();
    throw new AuthRequiredError();
  }
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
}

// ===========================================
// Infinite Scroll
// ===========================================

function updateSentinelVisibility(): void {
  const shouldShow = searchQuery === "" && !archiveLoaded;
  scrollSentinelEl.hidden = !shouldShow;

  if (shouldShow) {
    observeSentinel();
  } else {
    disconnectObserver();
  }
}

function observeSentinel(): void {
  if (scrollObserver) return; // already observing

  scrollObserver = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) {
        loadMoreActivities();
      }
    },
    { rootMargin: "200px" },
  );
  scrollObserver.observe(scrollSentinelEl);
}

function disconnectObserver(): void {
  if (scrollObserver) {
    scrollObserver.disconnect();
    scrollObserver = null;
  }
}

function showSentinelState(state: "loading" | "error"): void {
  const spinner = scrollSentinelEl.querySelector(
    ".scroll-sentinel__spinner",
  ) as HTMLElement;
  const srSpan = scrollSentinelEl.querySelector(
    ".visually-hidden",
  ) as HTMLElement;
  if (state === "loading") {
    scrollSentinelEl.hidden = false;
    spinner.hidden = false;
    if (srSpan) srSpan.hidden = false;
    scrollSentinelEl.querySelector(".scroll-sentinel__error")?.remove();
  } else {
    spinner.hidden = true;
    if (srSpan) srSpan.hidden = true;
    // Remove any existing error before adding new one
    scrollSentinelEl.querySelector(".scroll-sentinel__error")?.remove();
    const errorEl = document.createElement("p");
    errorEl.className = "scroll-sentinel__error";
    errorEl.innerHTML = t("load_error_retry");
    scrollSentinelEl.appendChild(errorEl);
    // Attach retry handler
    const retryLink = errorEl.querySelector("#scroll-retry");
    if (retryLink) {
      retryLink.addEventListener("click", (e) => {
        e.preventDefault();
        observeSentinel();
        loadMoreActivities();
      });
    }
  }
}

// ===========================================
// Event Handlers
// ===========================================

function showLandingView(): void {
  showView("landing");
  loadingEl.hidden = true;
  errorEl.hidden = true;
  updateAlertBanner();
}

async function loadActivities(): Promise<void> {
  setUIState("loading");

  try {
    const data = await fetchActivities();
    allActivities = data.activities.map(toRenderableActivity);

    // Track pagination cursor from initial load
    if (data.pagination?.hasMore && data.pagination.nextCursor) {
      archiveCursor = data.pagination.nextCursor;
      archiveLoaded = false;
    } else {
      archiveLoaded = true;
    }

    if (data.isTestEnvironment) {
      demoBannerEl.hidden = false;
    }

    if (allActivities.length === 0) {
      setUIState("empty");
    } else {
      renderGroupedActivities(allActivities);
      setUIState("loaded");
    }

    // Start session keepalive heartbeat + activity tracking (confirmed authenticated)
    startSessionKeepalive();
    startActivityTracking();
  } catch (error) {
    if (error instanceof AuthRequiredError) return;
    console.error("Failed to load activities:", error);
    setUIState("error");
  }
}

async function loadMoreActivities(): Promise<void> {
  if (archiveLoaded || isLoadingMore) return;

  isLoadingMore = true;
  showSentinelState("loading");

  try {
    const archiveData = await fetchMoreActivities();
    allActivities = [
      ...allActivities,
      ...archiveData.activities.map(toRenderableActivity),
    ];
    if (archiveData.pagination?.hasMore && archiveData.pagination.nextCursor) {
      archiveCursor = archiveData.pagination.nextCursor;
    } else {
      archiveLoaded = true;
    }
    renderGroupedActivities(allActivities);
  } catch (error) {
    if (error instanceof AuthRequiredError) return;
    console.error("Failed to load more activities:", error);
    showSentinelState("error");
    disconnectObserver(); // stop observing until retry
  } finally {
    isLoadingMore = false;
  }
}

function handleEventClick(index: number): void {
  const event = allActivities[index];
  if (!event) return;

  listScrollPosition = window.scrollY;
  renderDetail(event);
  history.pushState({ view: "detail", index } satisfies HistoryState, "");
  showView("detail");
  window.scrollTo(0, 0);
}

function handleBackToList(): void {
  history.back();
}

let searchTimeout: ReturnType<typeof setTimeout> | null = null;

function handleSearchInput(): void {
  const query = searchInputEl.value.trim();
  searchClearEl.hidden = query.length === 0;

  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    searchQuery = query;
    renderGroupedActivities(allActivities);
  }, SEARCH_DEBOUNCE_MS);
}

function handleSearchClear(): void {
  searchInputEl.value = "";
  searchClearEl.hidden = true;
  searchQuery = "";
  renderGroupedActivities(allActivities);
  searchInputEl.focus();
}

// ===========================================
// Login Loader
// ===========================================

function initLoginButton(): void {
  initLoaderRestoreReset();

  const loginBtn = document.getElementById("login-btn");
  if (loginBtn) {
    loginBtn.addEventListener("click", (e) => {
      e.preventDefault();
      showButtonLoader(t("loading_login"));
      window.location.href = "/api/auth/login";
    });
  }
}

// ===========================================
// Idle Model Wiring
// ===========================================

function initIdleWiring(): void {
  const stayBtn = document.getElementById("idle-stay-btn");
  if (stayBtn) {
    stayBtn.addEventListener("click", () => {
      void stayActiveAfterHeartbeat();
    });
  }

  const idleDialog = document.getElementById("idle-warning");
  if (idleDialog instanceof HTMLDialogElement) {
    idleDialog.addEventListener("cancel", (e) => {
      e.preventDefault();
      void stayActiveAfterHeartbeat();
    });
  }
}

// ===========================================
// Initialize
// ===========================================

async function init(): Promise<void> {
  translate(dictionaries);

  initThemeToggle();
  initLanguageToggle();
  initLoginButton();
  initIdleWiring();

  // Check authentication before showing any view
  const isAuthenticated = await checkSession();
  if (!isAuthenticated) {
    showLandingView();
    return;
  }

  // User is authenticated — show the activity list view
  showView("list");

  history.replaceState({ view: "list" } satisfies HistoryState, "");

  if (
    !(window as unknown as Record<string, unknown>).__argusPopstateRegistered
  ) {
    (window as unknown as Record<string, unknown>).__argusPopstateRegistered =
      true;
    window.addEventListener("popstate", (e: PopStateEvent) => {
      const state = e.state as HistoryState | null;
      if (state?.view === "detail") {
        const event = allActivities[state.index];
        if (event) {
          renderDetail(event);
          showView("detail");
          window.scrollTo(0, 0);
        } else {
          showView("list");
        }
      } else {
        showView("list");
      }
    });
  }

  retryBtn.addEventListener("click", loadActivities);
  detailBackBtn.addEventListener("click", handleBackToList);
  searchInputEl.addEventListener("input", handleSearchInput);
  searchClearEl.addEventListener("click", handleSearchClear);

  activityListEl.addEventListener("click", (e: Event) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>(".event-row");
    if (target) {
      const index = Number(target.dataset.eventIndex);
      handleEventClick(index);
    }
  });

  activityListEl.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      const target = (e.target as HTMLElement).closest<HTMLElement>(
        ".event-row",
      );
      if (target) {
        e.preventDefault();
        const index = Number(target.dataset.eventIndex);
        handleEventClick(index);
      }
    }
  });

  loadActivities();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
